<template>
    <div class="flex-grow-1 d-flex flex-column mw-100">
        <!-- No Pre-Content Video - Normal Display -->
        <div v-if="!showVideoFirst || reviewMode" class="flex-grow-1 d-flex mw-100">
            <div class="flex-grow-1 flex-column d-flex mw-100">
                <div
                    :class="{
                        card: showHeaders,
                        'bg-transparent': noPadding,
                        'flex-grow-1': !noPadding,
                        'flex-grow-0': noPadding,
                    }"
                >
                    <div
                        class="d-flex flex-column"
                        :class="{
                            'card-body p-3 panel-card-body': showHeaders && !noPadding,
                            'justify-content-center': panel.vertical_align === 'center',
                        }"
                    >
                        <div class="rich-text-content mb-4" v-show="showSubheader" v-html="panel.sub_header"></div>
                        <slot name="content"></slot>
                    </div>
                </div>
                <slot name="footer"></slot>
            </div>
        </div>

        <!-- Full Screen Pre-Content Video -->
        <div v-else-if="showFullScreenVideoFirst">
            <div
                class="video-overlay-fullscreen video-overlay align-items-center justify-content-center d-flex flex-column"
                v-if="preContentVideoComplete"
            >
                <div
                    class="d-flex flex-column position-relative flex-grow-0 card card-body shadow-lg rounded-lg p-0 video-content-container default-cursor"
                    :class="{ 'panel-card-smooth': isResizingPanel }"
                    :style="{ width: panel.width + 'px' }"
                >
                    <!-- Panel Editor Toolbar -->
                    <div
                        v-if="editMode"
                        class="shadow panel-toolbar"
                        :class="{ 'panel-toolbar-hide': isResizingPanel }"
                    >
                        <div class="btn-group" role="group" aria-label="Content action toolbar">
                            <button
                                type="button"
                                class="btn btn-dark"
                                aria-label="Edit content"
                                @click="emitEditAction('edit')"
                            >
                                <span class="icon icon-lg icon-glyphicons-basic-pencil align-middle"></span>
                            </button>
                            <button
                                type="button"
                                class="btn btn-dark"
                                aria-label="Clone content"
                                @click="emitEditAction('clone')"
                            >
                                <span class="icon icon-lg icon-glyphicons-basic-squares align-middle"></span>
                            </button>
                            <button
                                type="button"
                                class="btn btn-dark"
                                aria-label="Remove content"
                                @click="emitEditAction('delete')"
                            >
                                <span class="icon icon-lg icon-glyphicons-basic-bin align-middle"></span>
                            </button>
                        </div>
                    </div>
                    <!-- Left Resize Handle -->
                    <div
                        v-if="editMode"
                        class="resize-handle-show resize-handle-container resize-handle-container-left"
                    >
                        <div class="resize-handle"></div>
                    </div>
                    <!-- Content -->
                    <div class="fs-video-overlay-content" ref="fsOverlayContent" @scroll="scrollFsContentCb()">
                        <div
                            class="rich-text-content mb-3 pb-1 w-100"
                            v-show="panel.sub_header !== '' && panel.sub_header != '<p></p>'"
                            v-html="panel.sub_header"
                        ></div>
                        <div :class="{ 'disable-pointer': isResizingPanel }">
                            <slot name="content"></slot>
                        </div>
                        <div class="d-flex justify-content-end w-100">
                            <slot name="footer"></slot>
                        </div>
                        <!-- Navigation Buttons -->
                        <div
                            class="d-flex align-items-center justify-content-center buttons-container mb-2"
                        >
                            <button class="btn btn-transparent btn-session" @click="restartPreContentVideo()">
                                <span aria-hidden="true" class="icon icon-glyphicons-basic-restart mr-1"></span>
                                <span>{{ labelsTranslated.replay }}</span>
                            </button>
                            <div>
                                <!-- Continue/Submit Button -->
                                <session-footer-btns
                                    class="ml-1"
                                    v-show="!loading"
                                    @continue="handleContinue()"
                                    @submit="handleSubmit()"
                                ></session-footer-btns>
                            </div>
                        </div>
                    </div>
                    <!-- Right Resize Handle -->
                    <div
                        v-if="editMode"
                        class="resize-handle-container resize-handle-show resize-handle-container-right"
                    >
                        <div class="resize-handle"></div>
                    </div>
                    <!-- Scroll Down Message -->
                    <div
                        class="scrolldown-msg shadow bg-dark rounded"
                        :class="{
                            'scrolldown-msg-show': showScrollDownMsg,
                            'scrolldown-msg-hide': !showScrollDownMsg,
                        }"
                    >
                        <div class="d-flex align-items-center text-light py-1 px-2 nowrap">
                            <span
                                aria-hidden="true"
                                class="icon icon-glyphicons-basic-arrow-thin-down align-middle mr-1 scrolldown-icon"
                            ></span>
                            <span class="align-middle">{{ labelsTranslated.scrollDown }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Pre-Content Video -->
            <div class="embedded-video-container embedded-video-container-fullscreen rounded-sm">
                <rich-content-display
                    v-if="preContentVideo"
                    :elements="[preContentVideo]"
                    v-on:video-complete="handleVideoComplete()"
                    v-on:video-restart="handleVideoRestart()"
                    :skip-media-to-end="currentNodeIsPreviouslyVisited || preContentVideoComplete"
                    :close-full-screen-videos-after-end="nodeHasMultiplePanels"
                    ref="preContentVideo"
                ></rich-content-display>
            </div>
        </div>

        <!-- Non-Fullscreen Pre-Content Video -->
        <div v-else-if="showVideoFirst" class="card">
            <div class="d-flex flex-column">
                <div class="position-relative">
                    <div
                        class="video-overlay"
                        :class="{ 'd-flex': preContentVideoComplete }"
                        v-show="preContentVideoComplete"
                    >
                        <div class="flex-grow-1 d-flex justify-content-center align-items-center mw-100">
                            <div
                                class="d-flex flex-column position-relative flex-grow-0 card card-body shadow pb-3 rounded-lg video-content-container non-fullscreen-video-content mx-1 mx-sm-3"
                            >
                                <!-- Panel Header, Content, and Footer -->
                                <div
                                    class="rich-text-content mb-3 pb-1 w-100"
                                    v-show="panel.sub_header !== '' && panel.sub_header != '<p></p>'"
                                    v-html="panel.sub_header"
                                ></div>
                                <slot name="content"></slot>
                                <div class="d-flex justify-content-center buttons-container">
                                    <button class="btn btn-transparent btn-session" @click="restartPreContentVideo()">
                                        <span aria-hidden="true" class="icon icon-glyphicons-basic-restart mr-1"></span>
                                        <span>{{ labelsTranslated.replay }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Pre-Content Video -->
                    <div class="embedded-video-container rounded-sm">
                        <rich-content-display
                            v-if="preContentVideo"
                            :elements="[preContentVideo]"
                            v-on:video-complete="handleVideoComplete()"
                            v-on:video-restart="handleVideoRestart()"
                            :skip-media-to-end="currentNodeIsPreviouslyVisited || preContentVideoComplete"
                            :close-full-screen-videos-after-end="nodeHasMultiplePanels"
                            ref="preContentVideo"
                        ></rich-content-display>
                    </div>
                </div>
            </div>
        </div>
        <slot name="modal"></slot>
    </div>
</template>

<script>
import http from "@/axios.js"
import interact from "interactjs"
import RichContentDisplay from "@/components/RichContentDisplay"
import SessionFooterBtns from "@/components/SessionFooterBtns"

export default {
    name: "BasePanel",
    components: {
        RichContentDisplay,
        SessionFooterBtns,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        // This flag is set to true when the learners responses for a panel are being shown for review.
        // When true, only the activity/inquiry associated with the panel is shown - the header,
        // subheader, and pre-content video are all hidden/removed from view.
        reviewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        noPadding: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            preContentVideoComplete: this.$store.getters.preContentVideoComplete,
            isResizingPanel: false,
            resizablePanelInteract: null,
            showScrollDownMsg: false,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        nodeHasMultiplePanels() {
            return this.$store.getters.sessionCurrentPanels.length > 1
        },
        currentSession() {
            return this.$store.getters.session
        },
        currentNode() {
            return this.currentSession.current_node
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        currentNodeIsPreviouslyVisited() {
            return this.currentSession.current_node_was_previously_visited
        },
        editMode() {
            return this.$store.getters.previewEditMode
        },
        // Returns true when a video should be shown before the main content or activity
        // within the panel.
        showVideoFirst() {
            return this.panel.show_video_first && this.preContentVideo && !this.reviewMode
        },
        // Returns true when a *fullscreen* video should be shown before the main content or activity
        // within the panel.
        showFullScreenVideoFirst() {
            if (!this.preContentVideo || !this.showVideoFirst) {
                return false
            }
            return this.preContentVideo.display_full_screen
        },
        // Returns the video that should be shown before the main content or activity.
        preContentVideo() {
            return this.panel.pre_content_videos ? this.panel.pre_content_videos[0] : null
        },
        // Controls the display of headers - depends on whether the panel is being displayed in
        // review mode.
        showHeaders() {
            return !this.reviewMode
        },
        showSubheader() {
            return this.panel.sub_header !== "" && this.panel.sub_header != "<p></p>" && this.showHeaders
        },
    },
    methods: {
        handleVideoRestart() {
            this.preContentVideoComplete = false
        },
        handleVideoComplete() {
            this.$store.commit("setPreContentVideoComplete", true)
            this.$emit("video-complete")
            this.preContentVideoComplete = true
            let vm = this
            this.$nextTick(() => {
                // Signal to the parent component that content was shown.
                vm.$emit("content-shown")
                // Check if the shown content exceeds the available height of the fullscreen
                // video overlay element - if so, show a message to the user to scroll down.
                if (this.$refs.fsOverlayContent) {
                    const element = this.$refs.fsOverlayContent
                    if (element.scrollHeight > element.clientHeight) {
                        // Show the message after a delay to better capture the user's attention.
                        window.setTimeout(() => {
                            vm.showScrollDownMsg = true
                        }, 500)
                    } else {
                        vm.showScrollDownMsg = false
                    }
                }
            })
        },
        restartPreContentVideo() {
            this.$refs.preContentVideo.restartVideos()
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleSubmit() {
            this.$emit("submit")
        },
        emitEditAction(action) {
            this.$emit("edit-action", action)
        },
        skipVideos() {
            if (this.$refs.preContentVideo) {
                this.$refs.preContentVideo.skipVideos()
            }
        },
        // Save the panel's new width in the db.
        savePanelWidth(newWidth) {
            if (!this.editMode) {
                return
            }
            http.put(`/api/panels/${this.panel.id}/basic/`, {
                width: newWidth,
            })
            this.panel.width = newWidth
        },
        // Initialize resize actions on the panel.
        initResizablePanel() {
            let vm = this
            this.resizablePanelInteract = interact(".video-content-container").resizable({
                // Resize from handles only.
                edges: { left: ".resize-handle-container-left", right: ".resize-handle-container-right" },
                margin: 10, // Controls the margin for drag handles.
                listeners: {
                    move(event) {
                        let target = event.target
                        target.style.width = event.rect.width + "px"
                    },
                    start() {
                        vm.isResizingPanel = true
                        vm.$store.commit("setLayoutResizeIsActive", true)
                    },
                    end(event) {
                        vm.isResizingPanel = false
                        vm.savePanelWidth(event.rect.width)
                        vm.$store.commit("setLayoutResizeIsActive", false)
                    },
                },
                modifiers: [
                    interact.modifiers.snapSize({
                        targets: [{ width: 50 }, interact.snappers.grid({ width: 50, height: 50 })],
                    }),
                    interact.modifiers.restrictSize({
                        min: { width: 550 },
                        max: { width: 900 },
                    }),
                ],
            })
        },
        // Used to hide the 'scroll down' message when the user scrolls the fullscreen
        // video content overlay.
        scrollFsContentCb() {
            this.showScrollDownMsg = false
        },
    },
    watch: {
        editMode: function (val) {
            if (val && this.resizablePanelInteract) {
                // Enable column resize actions.
                this.resizablePanelInteract.resizable(true)
            } else if (!val && this.resizablePanelInteract) {
                // Disable column resize actions.
                this.resizablePanelInteract.resizable(false)
            } else {
                // Initialize (and enable) column resize actions.
                this.initResizablePanel()
            }
        },
    },
    created() {
        let vm = this
        if (!vm.showVideoFirst) {
            // There's no pre-content video to display, so signal to the parent component that
            // content is shown.
            vm.$nextTick(() => vm.$emit("content-shown"))
        }
    },
    mounted() {
        if (this.currentNodeIsPreviouslyVisited) {
            // If the current node was visited before, pre-content videos are skipped and
            // content is shown immediately.
            this.preContentVideoComplete = true
        }
        if (this.editMode) {
            this.initResizablePanel()
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/video.scss";
@import "@/assets/styles/resize.scss";
@import "@/assets/styles/paneltoolbar.scss";

$drag-transition-time: 0.3s;

.buttons-container {
    padding-top: 36px;
}

.video-content-container {
    background-color: #ffffff;
    max-width: 100%;
    @media (max-width: $breakpoint-sm) {
        // Make sure to use almost the full screen width on mobile.
        min-width: 96vw;
        max-width: 96vw;
    }
    @media (min-width: $breakpoint-sm) {
        max-width: 95%;
    }
    overflow-y: auto;
    overflow: visible;
}

.video-overlay {
    .card-body {
        @media (max-width: $breakpoint-sm) {
            padding: 0.75rem;
        }
        padding: 1rem;
    }
    z-index: 20 !important;
    opacity: 1 !important;
    background: transparent !important;
}

.video-overlay-fullscreen {
    position: fixed;
    padding-top: 70px;
    padding-bottom: 70px;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    background: #212121;
}

.video-content-container {
    &:hover {
        .panel-toolbar {
            opacity: 1;
        }
    }
}

.fs-video-overlay-content {
    overflow-y: auto;
    padding: 1rem;
}

.non-fullscreen-video-content {
    min-width: 400px;
    max-width: 700px;
}

.embedded-video-container-fullscreen {
    cursor: default;
}

.resize-handle-container-left {
    left: -8px;
}

.resize-handle-container-right {
    right: -8px;
}

.panel-card-smooth {
    transition: all $drag-transition-time !important;
}

.scrolldown-msg {
    position: absolute;
    z-index: 16;
    bottom: 18px;
    left: 50%;
    transform: translateX(-50%);
    transition: opacity 0.25s linear;
    pointer-events: none;
}

.scrolldown-msg-show {
    opacity: 1;
}

.scrolldown-msg-hide {
    opacity: 0;
}

.scrolldown-icon {
    animation: up-and-down 1.25s ease-in-out infinite;
}

@keyframes up-and-down {
    0%,
    100% {
        transform: translateY(2px);
    }
    50% {
        transform: translateY(-1px);
    }
}
</style>